import React, { useState, useEffect, useRef, useCallback } from 'react';
import { readDICOMFile, isMP4TransferSyntax, getTransferSyntaxDescription } from '../utils/cornerstoneInit';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import DicomUnifiedViewer from './DicomUnifiedViewer';
import DicomGridViewer from './DicomGridViewer';
import ViewerDrawer from './ViewerDrawer';
import { registerAHIImageLoader } from '../utils/ahiImageLoader';
import './DicomGallery.css';

/**
 * Gallery component that displays thumbnails for multiple DICOM files
 * and allows viewing them in different viewer modes
 */
const DicomGallery = ({ awsFiles = [], awsImageData = [] }) => {
    const [dicomFiles, setDicomFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
    const [showProperties, setShowProperties] = useState(false);
    const [showCritique, setShowCritique] = useState(false);
    const [critiques, setCritiques] = useState({}); // Store critiques by file ID
    const [viewMode, setViewMode] = useState('single'); // 'single' or 'grid'
    const [selectedFiles, setSelectedFiles] = useState([]); // For grid view
    const [gridLayout, setGridLayout] = useState({ rows: 1, cols: 1 }); // Default to 1x1
    const fileInputRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [ahiLoaderRegistered, setAhiLoaderRegistered] = useState(false);
    const [rightDrawerOpen, setRightDrawerOpen] = useState(true); // Track drawer state
    const gridViewerRef = useRef(null); // Ref for DicomGridViewer
    const unifiedViewerRef = useRef(null); // Ref for DicomUnifiedViewer
    const [playbackState, setPlaybackState] = useState({ isPlaying: false, currentFrame: 1, totalFrames: 1, frameRate: 30 });
    
    // Annotation state
    const [annotationMode, setAnnotationMode] = useState(false);
    const [activeTool, setActiveTool] = useState('WindowLevel');
    const [currentColor, setCurrentColor] = useState('#FF0000');
    const [currentLineWidth, setCurrentLineWidth] = useState(2);

    // Initialize cornerstone for thumbnail generation
    useEffect(() => {
        const initializeCornerstone = async () => {
            try {
                if (!isInitialized && typeof cornerstoneWADOImageLoader !== 'undefined') {
                    // Configure cornerstone-wado-image-loader
                    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
                    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

                    // Only initialize if not already initialized
                    if (cornerstoneWADOImageLoader.webWorkerManager) {
                        cornerstoneWADOImageLoader.webWorkerManager.terminate();
                        cornerstoneWADOImageLoader.webWorkerManager.initialize({
                            maxWebWorkers: 0,
                            startWebWorkersOnDemand: false,
                            webWorkerPath: null,
                            webWorkerTaskPaths: [],
                            taskConfiguration: {
                                decodeTask: {
                                    initializeCodecsOnStartup: false
                                }
                            }
                        });
                    }

                    cornerstoneWADOImageLoader.configure({
                        useWebWorkers: false,
                        decodeConfig: {}
                    });

                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('Error initializing cornerstone for thumbnails:', error);
            }
        };

        initializeCornerstone();
    }, [isInitialized]);

    // Register AHI loader once
    useEffect(() => {
        if (!ahiLoaderRegistered && isInitialized) {
            registerAHIImageLoader();
            setAhiLoaderRegistered(true);
        }
    }, [isInitialized, ahiLoaderRegistered]);

    // Handle AWS files on mount (traditional DICOM files)
    useEffect(() => {
        if (awsFiles && awsFiles.length > 0 && isInitialized) {
            console.log('Loading AWS files:', awsFiles.length);
            handleFileSelect({ target: { files: awsFiles } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awsFiles, isInitialized]);

    // Handle AHI image data
    useEffect(() => {
        const loadAHIImages = async () => {
            if (awsImageData && awsImageData.length > 0 && isInitialized && ahiLoaderRegistered) {
                console.log('Loading AWS HealthImaging data:', awsImageData.length);

                setIsLoading(true);
                setLoadingProgress({ current: 0, total: awsImageData.length });

                const processedFiles = [];

                for (let i = 0; i < awsImageData.length; i++) {
                    const data = awsImageData[i];
                    setLoadingProgress({ current: i + 1, total: awsImageData.length });

                    try {
                        if (data.type === 'video') {
                            // Handle video data - create a blob for video playback
                            const blob = new Blob([data.pixelData], { type: data.contentType || 'video/mp4' });
                            const file = new File([blob], `video_${i}.mp4`, { type: data.contentType || 'video/mp4' });

                            // Generate thumbnail for video
                            let thumbnailUrl = null;
                            try {
                                thumbnailUrl = await generateAHIVideoThumbnail(blob);
                            } catch (thumbError) {
                                console.error('Error generating AHI video thumbnail:', thumbError);
                            }

                            processedFiles.push({
                                file,
                                id: data.frameId,
                                filename: `Video Frame ${data.metadata.instanceNumber || i + 1}`,
                                isMP4: true,
                                isAHI: true,
                                numberOfFrames: data.metadata.numberOfFrames || 1,
                                dimensions: `${data.metadata.columns || '?'} x ${data.metadata.rows || '?'}`,
                                instanceNumber: data.metadata.instanceNumber,
                                seriesDescription: data.metadata.seriesDescription || 'Video',
                                transferSyntaxDesc: 'MPEG-4 AVC/H.264',
                                fileSize: (data.pixelData.byteLength / (1024 * 1024)).toFixed(2) + ' MB',
                                thumbnailUrl,
                                // Pass the full metadata for the viewer
                                ahiMetadata: data.metadata
                            });
                        } else {
                            // Handle regular images - use the AHI image ID
                            // Generate thumbnail for AHI image
                            let thumbnailUrl = null;
                            try {
                                thumbnailUrl = await generateAHIImageThumbnail(data.imageId);
                            } catch (thumbError) {
                                console.error('Error generating AHI image thumbnail:', thumbError);
                            }

                            processedFiles.push({
                                imageId: data.imageId,  // Use imageId instead of file
                                id: data.imageId,
                                filename: `Instance ${data.metadata.instanceNumber || i + 1}`,
                                isMP4: false,
                                isAHI: true,
                                instanceNumber: data.metadata.instanceNumber,
                                seriesDescription: data.metadata.seriesDescription || 'Image',
                                numberOfFrames: 1,
                                dimensions: `${data.metadata.columns || '?'} x ${data.metadata.rows || '?'}`,
                                transferSyntaxDesc: 'AHI Stream',
                                fileSize: 'Streamed',
                                thumbnailUrl,
                                // Pass the full metadata for the viewer
                                ahiMetadata: data.metadata
                            });
                        }
                    } catch (error) {
                        console.error('Error processing AHI data:', error);
                    }
                }

                setDicomFiles(processedFiles);
                setIsLoading(false);

                // Auto-select first file if none selected
                if (processedFiles.length > 0 && !selectedFile) {
                    setSelectedFile(processedFiles[0]);
                }
            }
        };

        loadAHIImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awsImageData, isInitialized, ahiLoaderRegistered]);

    // Handle folder/multiple file selection (for local files)
    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setIsLoading(true);
        setLoadingProgress({ current: 0, total: files.length });
        setDicomFiles([]); // Clear existing files

        const processedFiles = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setLoadingProgress({ current: i + 1, total: files.length });

            try {
                // Read DICOM metadata
                const dicomData = await readDICOMFile(file);

                // Extract metadata
                const transferSyntax = dicomData.string('x00020010');
                const transferSyntaxDesc = getTransferSyntaxDescription(dicomData);
                const sopInstanceUID = dicomData.string('x00080018');
                // Get SOP Class UID from the main dataset (not just media storage)
                const sopClassUID = dicomData.string('x00080016') || dicomData.string('x00020002'); // SOP Class UID or Media Storage SOP Class UID as fallback
                const seriesDescription = dicomData.string('x0008103e') || 'No description';
                const patientName = dicomData.string('x00100010') || 'Unknown';
                const studyDate = dicomData.string('x00080020') || '';

                // Get Instance Number for sorting
                let fileInstanceNumber = null;
                try {
                    const instanceNumberStr = dicomData.string('x00200013');
                    if (instanceNumberStr) {
                        fileInstanceNumber = parseInt(instanceNumberStr, 10);
                    }
                } catch (error) {
                    console.log('Could not read instance number');
                }

                // Robust handling of number of frames
                let numberOfFrames = 1;
                try {
                    if (dicomData.string('x00280008')) {
                        numberOfFrames = parseInt(dicomData.string('x00280008'), 10);
                    } else if (dicomData.elements.x00280008) {
                        numberOfFrames = dicomData.uint16('x00280008');
                    }
                    if (isNaN(numberOfFrames) || numberOfFrames < 1) {
                        numberOfFrames = 1;
                    }
                } catch (error) {
                    numberOfFrames = 1;
                }

                const rows = dicomData.uint16('x00280010');
                const columns = dicomData.uint16('x00280011');
                const isMP4 = isMP4TransferSyntax(dicomData);

                // Generate thumbnail
                let thumbnailUrl = null;
                try {
                    thumbnailUrl = await generateThumbnail(file, dicomData, isMP4);
                } catch (thumbError) {
                    console.error('Error generating thumbnail for', file.name, thumbError);
                }

                processedFiles.push({
                    file,
                    id: sopInstanceUID || `file-${i}`,
                    filename: file.name,
                    transferSyntax,
                    transferSyntaxDesc,
                    sopInstanceUID,
                    sopClassUID, // Add SOP Class UID for multiframe detection
                    seriesDescription,
                    patientName,
                    studyDate,
                    numberOfFrames,
                    dimensions: `${columns} x ${rows}`,
                    isMP4,
                    isAHI: false, // Local file, not from AHI
                    thumbnailUrl,
                    fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
                    instanceNumber: fileInstanceNumber
                });
            } catch (error) {
                console.error('Error processing file', file.name, error);
                // Add file with error state
                processedFiles.push({
                    file,
                    id: `file-${i}`,
                    filename: file.name,
                    error: error.message,
                    fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
                    instanceNumber: null,
                    isAHI: false
                });
            }
        }

        // Sort files by Instance Number (0020,0013)
        processedFiles.sort((a, b) => {
            // Files with errors go to the end
            if (a.error && !b.error) return 1;
            if (!a.error && b.error) return -1;

            // If both have instance numbers, sort numerically
            if (a.instanceNumber !== null && b.instanceNumber !== null) {
                return a.instanceNumber - b.instanceNumber;
            }

            // Files with instance numbers come before those without
            if (a.instanceNumber !== null && b.instanceNumber === null) return -1;
            if (a.instanceNumber === null && b.instanceNumber !== null) return 1;

            // If neither has instance number, maintain original order
            return 0;
        });

        setDicomFiles(processedFiles);
        setIsLoading(false);

        // Auto-select first file if none selected
        if (processedFiles.length > 0 && !selectedFile) {
            setSelectedFile(processedFiles[0]);
        }
    };

    // Generate thumbnail for a DICOM file
    const generateThumbnail = async (file, dicomData, isMP4) => {
        if (isMP4) {
            // For MP4 files, extract the first frame
            return await extractVideoThumbnail(file, dicomData);
        } else {
            // For non-MP4 files, render the actual image
            return await extractImageThumbnail(file);
        }
    };

    // Generate thumbnail for AHI video
    const generateAHIVideoThumbnail = async (blob) => {
        return new Promise((resolve, reject) => {
            const videoUrl = URL.createObjectURL(blob);
            const video = document.createElement('video');
            video.src = videoUrl;
            video.crossOrigin = 'anonymous';

            video.addEventListener('loadeddata', () => {
                video.currentTime = 0;
            });

            video.addEventListener('seeked', () => {
                const canvas = document.createElement('canvas');
                const size = 100;
                canvas.width = size;
                canvas.height = size;

                const ctx = canvas.getContext('2d');
                const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
                const x = (canvas.width / 2) - (video.videoWidth * scale / 2);
                const y = (canvas.height / 2) - (video.videoHeight * scale / 2);

                ctx.fillStyle = '#000';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale);

                const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
                URL.revokeObjectURL(videoUrl);
                video.remove();
                resolve(thumbnailUrl);
            });

            video.addEventListener('error', () => {
                URL.revokeObjectURL(videoUrl);
                reject(new Error('Failed to load video'));
            });

            video.load();
        });
    };

    // Generate thumbnail for AHI image
    const generateAHIImageThumbnail = async (imageId) => {
        try {
            // Load the image using cornerstone
            const loadedImage = await cornerstone.loadImage(imageId);
            const image = loadedImage.promise ? await loadedImage.promise : loadedImage;

            // Create canvas for thumbnail
            const canvas = document.createElement('canvas');
            const size = 100;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');

            // Create a temporary canvas for the full image
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = image.width;
            tempCanvas.height = image.height;
            const tempCtx = tempCanvas.getContext('2d');

            // Get pixel data
            const pixelData = image.getPixelData();
            const imageData = tempCtx.createImageData(image.width, image.height);

            // Handle different image types
            if (image.color) {
                // Color image
                for (let i = 0; i < pixelData.length; i++) {
                    imageData.data[i] = pixelData[i];
                }
            } else {
                // Grayscale image
                let min = Number.MAX_VALUE;
                let max = Number.MIN_VALUE;

                // Find min/max for window leveling
                for (let i = 0; i < pixelData.length; i++) {
                    if (pixelData[i] < min) min = pixelData[i];
                    if (pixelData[i] > max) max = pixelData[i];
                }

                const range = max - min;
                if (range === 0) {
                    // Handle edge case where all pixels have same value
                    for (let i = 0, j = 0; i < pixelData.length; i++, j += 4) {
                        imageData.data[j] = 128;
                        imageData.data[j + 1] = 128;
                        imageData.data[j + 2] = 128;
                        imageData.data[j + 3] = 255;
                    }
                } else {
                    // Convert to RGBA
                    for (let i = 0, j = 0; i < pixelData.length; i++, j += 4) {
                        const normalized = ((pixelData[i] - min) / range) * 255;
                        imageData.data[j] = normalized;
                        imageData.data[j + 1] = normalized;
                        imageData.data[j + 2] = normalized;
                        imageData.data[j + 3] = 255;
                    }
                }
            }

            tempCtx.putImageData(imageData, 0, 0);

            // Scale to thumbnail size
            const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
            const x = (canvas.width / 2) - (image.width * scale / 2);
            const y = (canvas.height / 2) - (image.height * scale / 2);

            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(tempCanvas, x, y, image.width * scale, image.height * scale);

            return canvas.toDataURL('image/jpeg', 0.8);
        } catch (error) {
            console.error('Error generating AHI thumbnail:', error);
            throw error;
        }
    };

    // Extract thumbnail from MP4 DICOM
    const extractVideoThumbnail = async (file, dicomData) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async function (e) {
                try {
                    const arrayBuffer = e.target.result;
                    const pixelDataElement = dicomData.elements.x7fe00010;

                    if (!pixelDataElement || !pixelDataElement.dataOffset) {
                        reject(new Error('No pixel data found'));
                        return;
                    }

                    const pixelDataOffset = pixelDataElement.dataOffset;

                    // Look for MP4 header
                    const dataView = new DataView(arrayBuffer);
                    let mp4Start = pixelDataOffset;
                    const searchRange = Math.min(1000, arrayBuffer.byteLength - pixelDataOffset);

                    for (let i = 0; i < searchRange - 3; i++) {
                        if (String.fromCharCode(dataView.getUint8(pixelDataOffset + i)) === 'f' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 1)) === 't' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 2)) === 'y' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 3)) === 'p') {
                            mp4Start = pixelDataOffset + i - 4;
                            break;
                        }
                    }

                    // Extract MP4 data
                    const mp4Data = arrayBuffer.slice(mp4Start);
                    const blob = new Blob([mp4Data], { type: 'video/mp4' });
                    const videoUrl = URL.createObjectURL(blob);

                    // Create video element to extract frame
                    const video = document.createElement('video');
                    video.src = videoUrl;
                    video.crossOrigin = 'anonymous';

                    video.addEventListener('loadeddata', () => {
                        // Seek to first frame
                        video.currentTime = 0;
                    });

                    video.addEventListener('seeked', () => {
                        // Create canvas and extract frame
                        const canvas = document.createElement('canvas');
                        const size = 100; // Always use small size for video thumbnails
                        canvas.width = size;
                        canvas.height = size;

                        const ctx = canvas.getContext('2d');

                        // Calculate scaling to maintain aspect ratio
                        const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
                        const x = (canvas.width / 2) - (video.videoWidth * scale / 2);
                        const y = (canvas.height / 2) - (video.videoHeight * scale / 2);

                        ctx.fillStyle = '#000';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale);

                        // Convert to data URL
                        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);

                        // Clean up
                        URL.revokeObjectURL(videoUrl);
                        video.remove();

                        resolve(thumbnailUrl);
                    });

                    video.addEventListener('error', () => {
                        URL.revokeObjectURL(videoUrl);
                        reject(new Error('Failed to load video'));
                    });

                    // Start loading the video
                    video.load();
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(file);
        });
    };

    // Extract thumbnail from non-MP4 DICOM
    const extractImageThumbnail = async (file) => {
        return new Promise((resolve, reject) => {
            try {
                const imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);

                cornerstone.loadImage(imageId).then((image) => {
                    // Create canvas for thumbnail
                    const canvas = document.createElement('canvas');
                    const size = 100; // Always use small size
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');

                    // Create a temporary canvas for the full image
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = image.width;
                    tempCanvas.height = image.height;
                    const tempCtx = tempCanvas.getContext('2d');

                    // Get pixel data
                    const pixelData = image.getPixelData();
                    const imageData = tempCtx.createImageData(image.width, image.height);

                    // Handle different image types
                    if (image.color) {
                        // Color image
                        for (let i = 0; i < pixelData.length; i++) {
                            imageData.data[i] = pixelData[i];
                        }
                    } else {
                        // Grayscale image
                        let min = Number.MAX_VALUE;
                        let max = Number.MIN_VALUE;

                        // Find min/max for window leveling
                        for (let i = 0; i < pixelData.length; i++) {
                            if (pixelData[i] < min) min = pixelData[i];
                            if (pixelData[i] > max) max = pixelData[i];
                        }

                        const range = max - min;
                        if (range === 0) {
                            // Handle edge case where all pixels have same value
                            for (let i = 0, j = 0; i < pixelData.length; i++, j += 4) {
                                imageData.data[j] = 128;
                                imageData.data[j + 1] = 128;
                                imageData.data[j + 2] = 128;
                                imageData.data[j + 3] = 255;
                            }
                        } else {
                            // Convert to RGBA
                            for (let i = 0, j = 0; i < pixelData.length; i++, j += 4) {
                                const normalized = ((pixelData[i] - min) / range) * 255;
                                imageData.data[j] = normalized;
                                imageData.data[j + 1] = normalized;
                                imageData.data[j + 2] = normalized;
                                imageData.data[j + 3] = 255;
                            }
                        }
                    }

                    tempCtx.putImageData(imageData, 0, 0);

                    // Scale to thumbnail size
                    const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
                    const x = (canvas.width / 2) - (image.width * scale / 2);
                    const y = (canvas.height / 2) - (image.height * scale / 2);

                    ctx.fillStyle = '#000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(tempCanvas, x, y, image.width * scale, image.height * scale);

                    const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);

                    // Clean up
                    cornerstoneWADOImageLoader.wadouri.fileManager.remove(imageId);

                    resolve(thumbnailUrl);
                }).catch(reject);
            } catch (error) {
                reject(error);
            }
        });
    };

    // Handle thumbnail click
    const handleThumbnailClick = (dicomFile, event) => {
        // Always update the selected file
        setSelectedFile(dicomFile);

        // Always update the grid selection when a thumbnail is clicked
        // This ensures the grid shows consecutive images starting from the clicked thumbnail
        const currentIndex = dicomFiles.findIndex(f => f.id === dicomFile.id);
        if (currentIndex !== -1) {
            updateGridFromIndex(currentIndex);
        }
    };

    // When grid layout changes, ensure we populate the grid with files
    useEffect(() => {
        if (viewMode === 'grid' && selectedFile && dicomFiles.length > 0) {
            const currentIndex = dicomFiles.findIndex(f => f.id === selectedFile.id);
            if (currentIndex !== -1) {
                updateGridFromIndex(currentIndex);
            }
        }
    }, [gridLayout, viewMode, selectedFile, dicomFiles]);

    // Update grid to show consecutive images from a starting index
    const updateGridFromIndex = (startIndex) => {
        const totalViewports = gridLayout.rows * gridLayout.cols;
        const gridFiles = [];

        // Get consecutive images starting from the index, wrapping around if needed
        for (let i = 0; i < totalViewports; i++) {
            const fileIndex = (startIndex + i) % dicomFiles.length;
            if (dicomFiles[fileIndex]) {
                gridFiles.push(dicomFiles[fileIndex]);
            }
        }

        console.log(`Updating grid from index ${startIndex}, showing ${gridFiles.length} files in ${gridLayout.rows}x${gridLayout.cols} layout (with wrapping)`);
        setSelectedFiles(gridFiles);
    };

    // Toggle properties view
    const toggleProperties = () => {
        setShowProperties(!showProperties);
        setShowCritique(false);
    };

    // Toggle critique view
    const toggleCritique = () => {
        setShowCritique(!showCritique);
        setShowProperties(false);
    };

    // Handle critique text change
    const handleCritiqueChange = (fileId, text) => {
        setCritiques(prev => ({
            ...prev,
            [fileId]: text
        }));
    };

    // Reset playback state when switching files
    useEffect(() => {
        if (selectedFile) {
            // For multiframe files, default to playing state since they auto-play
            // For single frame files, default to not playing
            const isMultiframe = selectedFile.numberOfFrames > 1 || selectedFile.isMP4;
            const defaultState = isMultiframe 
                ? { isPlaying: true, currentFrame: 1, totalFrames: selectedFile.numberOfFrames || 40, frameRate: 30 }
                : { isPlaying: false, currentFrame: 1, totalFrames: 1, frameRate: 30 };
            
            setPlaybackState(defaultState);
            
            // Start polling after a short delay to allow viewer to initialize
            const timeoutId = setTimeout(() => {
                if (viewMode === 'grid' && gridViewerRef.current) {
                    try {
                        const state = gridViewerRef.current.getPlaybackState();
                        setPlaybackState(state);
                    } catch (e) {
                        // Grid viewer might not be ready yet
                    }
                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                    try {
                        const state = unifiedViewerRef.current.getPlaybackState();
                        setPlaybackState(state);
                    } catch (e) {
                        // Unified viewer might not be ready yet
                    }
                }
            }, 500); // Wait 500ms for viewer to initialize

            return () => clearTimeout(timeoutId);
        }
    }, [selectedFile, viewMode]);

    // Update playback state periodically for both modes to catch keyboard interactions
    useEffect(() => {
        let interval;
        if (viewMode === 'single' && unifiedViewerRef.current) {
            // For single mode, use polling to catch keyboard interactions
            interval = setInterval(() => {
                try {
                    const state = unifiedViewerRef.current.getPlaybackState();
                    setPlaybackState(state);
                } catch (e) {
                    // Unified viewer might not be ready yet
                }
            }, 500);
        } else if (viewMode === 'grid' && gridViewerRef.current) {
            // For grid mode, also use polling to catch keyboard interactions
            interval = setInterval(() => {
                try {
                    const state = gridViewerRef.current.getPlaybackState();
                    setPlaybackState(state);
                } catch (e) {
                    // Grid viewer might not be ready yet
                }
            }, 500);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [viewMode, selectedFile]);

    // Update playback state when active viewport changes in grid mode (event-driven)
    const updatePlaybackStateFromActiveViewport = useCallback(() => {
        if (viewMode === 'grid' && gridViewerRef.current) {
            try {
                const state = gridViewerRef.current.getPlaybackState();
                setPlaybackState(state);
            } catch (e) {
                // Grid viewer might not be ready yet
            }
        }
    }, [viewMode]);

    // Get active viewport file info for grid mode
    const getActiveViewportFileInfo = () => {
        if (viewMode === 'grid' && gridViewerRef.current) {
            try {
                const activeFileInfo = gridViewerRef.current.getActiveViewportFileInfo();
                return activeFileInfo;
            } catch (e) {
                return null;
            }
        }
        return selectedFile; // For single mode, return the selected file
    };

    // Clean up thumbnails on unmount
    useEffect(() => {
        return () => {
            dicomFiles.forEach(file => {
                if (file.thumbnailUrl && file.thumbnailUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(file.thumbnailUrl);
                }
            });
        };
    }, [dicomFiles]);

    return (
        <div className="dicom-gallery-container">
            {/* Left panel - Thumbnails */}
            <div className="thumbnails-panel">
                <div className="thumbnails-header">
                    <div className="view-buttons">
                        <button
                            className="view-button"
                            onClick={toggleCritique}
                            disabled={dicomFiles.length === 0}
                        >
                            {showCritique ? 'Hide Critique' : 'Critique'}
                        </button>
                    </div>
                </div>

                <div className={`thumbnails-grid ${showProperties ? 'show-properties' : ''} ${showCritique ? 'show-critique' : ''}`}>
                    {dicomFiles.map((dicomFile) => (
                        <div
                            key={dicomFile.id}
                            className={`thumbnail-item ${selectedFile?.id === dicomFile.id ? 'selected' : ''} ${dicomFile.error ? 'error' : ''}`}
                            onClick={(e) => !dicomFile.error && handleThumbnailClick(dicomFile, e)}
                            title={`Click to view`}
                        >
                            {dicomFile.thumbnailUrl ? (
                                <img
                                    src={dicomFile.thumbnailUrl}
                                    alt={dicomFile.filename}
                                    className="thumbnail-image"
                                />
                            ) : (
                                <div className="thumbnail-placeholder">
                                    {dicomFile.error ? '⚠️' : dicomFile.isAHI ? '☁️' : '📄'}
                                </div>
                            )}
                            {dicomFile.instanceNumber && (
                                <div className="instance-badge">{dicomFile.instanceNumber}</div>
                            )}
                            {!dicomFile.error && dicomFile.numberOfFrames > 1 && (
                                <div className="multiframe-indicator" title={`${dicomFile.numberOfFrames} frames`}>
                                    🎞️
                                </div>
                            )}
                            {dicomFile.isAHI && (
                                <div className="ahi-indicator" title="AWS HealthImaging">
                                    ☁️
                                </div>
                            )}
                            {showProperties && (
                                <div className="thumbnail-info">
                                    <p className="filename">{dicomFile.filename}</p>
                                    {!dicomFile.error ? (
                                        <>
                                            <p className="info-line">{dicomFile.seriesDescription}</p>
                                            <p className="info-line">{dicomFile.dimensions}</p>
                                            <p className="info-line">
                                                {dicomFile.numberOfFrames > 1
                                                    ? `🎞️ ${dicomFile.numberOfFrames} frames`
                                                    : '🖼️ Single frame'}
                                            </p>
                                            {dicomFile.instanceNumber && (
                                                <p className="info-line">Instance: #{dicomFile.instanceNumber}</p>
                                            )}
                                            {dicomFile.isAHI && (
                                                <p className="info-line">Source: AWS HealthImaging</p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="error-message">Error: {dicomFile.error}</p>
                                    )}
                                </div>
                            )}
                            {showCritique && (
                                <div className="thumbnail-critique">
                                    <p className="filename">{dicomFile.filename}</p>
                                    {!dicomFile.error ? (
                                        <textarea
                                            className="critique-textarea"
                                            placeholder="Add critique or comments for this image..."
                                            value={critiques[dicomFile.id] || ''}
                                            onChange={(e) => handleCritiqueChange(dicomFile.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()} // Prevent thumbnail selection when clicking textarea
                                        />
                                    ) : (
                                        <p className="error-message">Cannot critique - Error loading file</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {dicomFiles.length === 0 && !isLoading && (
                    <div className="empty-state">
                        <p>No DICOM files loaded</p>
                        <p>Click "Load DICOM Files" to browse</p>
                    </div>
                )}
            </div>

            {/* Right panel - Viewer */}
            <div className={`viewer-panel ${rightDrawerOpen ? 'right-drawer-open' : 'right-drawer-mini'}`}>
                {selectedFile || selectedFiles.length > 0 ? (
                    <div className="viewer-content">
                        {viewMode === 'grid' && selectedFiles.length > 0 ? (
                            <div className={`grid-viewer-wrapper ${rightDrawerOpen ? 'right-drawer-open' : ''}`}>
                                <DicomGridViewer
                                ref={gridViewerRef}
                                imageIds={selectedFiles.filter(f => f.imageId && f.isAHI).map(f => f.imageId)}
                                files={selectedFiles.filter(f => f.file && !f.isAHI).map(f => f.file)}
                                isAHI={selectedFiles.some(f => f.isAHI)}
                                initialLayout={gridLayout}
                                dicomFiles={selectedFiles}
                                allStudyFiles={dicomFiles}
                                drawerOpen={rightDrawerOpen}
                                onActiveViewportChange={updatePlaybackStateFromActiveViewport}
                                onLayoutChange={(newLayout) => {
                                    setGridLayout(newLayout);
                                    // Update grid files based on new layout
                                    if (selectedFile) {
                                        const currentIndex = dicomFiles.findIndex(f => f.id === selectedFile.id);
                                        const totalViewports = newLayout.rows * newLayout.cols;
                                        const gridFiles = [];

                                        // Get consecutive images starting from the index
                                        for (let i = 0; i < totalViewports && (currentIndex + i) < dicomFiles.length; i++) {
                                            gridFiles.push(dicomFiles[currentIndex + i]);
                                        }

                                        setSelectedFiles(gridFiles);
                                    }
                                }}
                            />
                            </div>
                        ) : (
                            selectedFile && (
                                <DicomUnifiedViewer
                                    ref={unifiedViewerRef}
                                    preloadedFile={selectedFile.file}
                                    preloadedImageId={selectedFile.imageId}
                                    fileName={selectedFile.filename}
                                    isAHI={selectedFile.isAHI}
                                    ahiMetadata={selectedFile.ahiMetadata}
                                    // Pass annotation props to the viewer
                                    annotationMode={annotationMode}
                                    activeTool={activeTool}
                                    currentColor={currentColor}
                                    currentLineWidth={currentLineWidth}
                                    onAnnotationModeChange={setAnnotationMode}
                                    onActiveToolChange={setActiveTool}
                                />
                            )
                        )}

                        {/* Always render the drawer separately */}
                        <ViewerDrawer
                            fileName={selectedFile?.filename}
                            onGridLayoutChange={(newLayout) => {
                                setGridLayout(newLayout);
                                setViewMode(newLayout.rows === 1 && newLayout.cols === 1 ? 'single' : 'grid');
                                if (newLayout.rows === 1 && newLayout.cols === 1) {
                                    setSelectedFiles([]);
                                } else {
                                    const currentIndex = dicomFiles.findIndex(f => f.id === selectedFile.id);
                                    if (currentIndex !== -1) {
                                        updateGridFromIndex(currentIndex);
                                    }
                                }
                            }}
                            currentGridLayout={gridLayout}
                            onDrawerToggle={(open) => setRightDrawerOpen(open)}
                            selectedFile={selectedFile}
                            fileInfo={selectedFile}
                            getActiveViewportFileInfo={getActiveViewportFileInfo}
                            // Playback controls - connect to grid viewer when in grid mode
                            isPlaying={playbackState.isPlaying}
                            currentFrame={playbackState.currentFrame}
                            totalFrames={playbackState.totalFrames}
                            frameRate={playbackState.frameRate}
                            onTogglePlayPause={() => {
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    gridViewerRef.current.togglePlayPause();
                                    // Update playback state
                                    setTimeout(() => {
                                        const state = gridViewerRef.current.getPlaybackState();
                                        setPlaybackState(state);
                                    }, 100);
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single view, use the unified viewer ref
                                    unifiedViewerRef.current.togglePlayPause();
                                    // Update playback state immediately
                                    setTimeout(() => {
                                        const state = unifiedViewerRef.current.getPlaybackState();
                                        setPlaybackState(state);
                                    }, 100);
                                }
                            }}
                            onFrameNavigation={(direction) => {
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    gridViewerRef.current.navigateFrame(direction);
                                    // Update playback state
                                    setTimeout(() => {
                                        const state = gridViewerRef.current.getPlaybackState();
                                        setPlaybackState(state);
                                    }, 100);
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single view, use the unified viewer ref
                                    unifiedViewerRef.current.navigateFrame(direction);
                                }
                            }}
                            onFrameSliderChange={(e) => {
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    gridViewerRef.current.handleFrameSliderChange(e);
                                    // Update playback state
                                    setTimeout(() => {
                                        const state = gridViewerRef.current.getPlaybackState();
                                        setPlaybackState(state);
                                    }, 100);
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single view, use the unified viewer ref
                                    unifiedViewerRef.current.handleFrameSliderChange(e);
                                }
                            }}
                            onFrameRateChange={(e) => {
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    gridViewerRef.current.handleFrameRateChange(e);
                                    // Update playback state
                                    setTimeout(() => {
                                        const state = gridViewerRef.current.getPlaybackState();
                                        setPlaybackState(state);
                                    }, 100);
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single view, use the unified viewer ref
                                    unifiedViewerRef.current.handleFrameRateChange(e);
                                }
                            }}
                            // Annotation tool props
                            activeTool={activeTool}
                            onSelectTool={setActiveTool}
                            annotationMode={annotationMode}
                            onToggleAnnotationMode={() => setAnnotationMode(!annotationMode)}
                            currentColor={currentColor}
                            onColorChange={(e) => setCurrentColor(e.target.value)}
                            currentLineWidth={currentLineWidth}
                            onLineWidthChange={(e) => setCurrentLineWidth(parseInt(e.target.value))}
                            onClearAnnotations={() => {
                                // Clear annotations in the active viewer
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    // For grid mode, clear annotations in the active viewport
                                    gridViewerRef.current.clearAnnotations();
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single mode, clear annotations in the unified viewer
                                    unifiedViewerRef.current.clearAnnotations();
                                }
                            }}
                            onToggleAnnotationVisibility={() => {
                                // Toggle annotation visibility in the active viewer
                                if (viewMode === 'grid' && gridViewerRef.current) {
                                    // For grid mode, toggle annotations in the active viewport
                                    gridViewerRef.current.toggleAnnotationVisibility();
                                } else if (viewMode === 'single' && unifiedViewerRef.current) {
                                    // For single mode, toggle annotations in the unified viewer
                                    unifiedViewerRef.current.toggleAnnotationVisibility();
                                }
                            }}
                            onResetViewport={() => {
                                // Reset viewport - this would need to be implemented in the viewer
                                console.log('Reset viewport requested');
                            }}
                            onInvertImage={() => {
                                // Invert image - this would need to be implemented in the viewer
                                console.log('Invert image requested');
                            }}
                        />
                    </div>
                ) : (
                    <div className="no-selection">
                        <p>Select a DICOM file to view</p>
                        <p className="hint">Use Ctrl+Click for multi-select, Shift+Click for range select</p>
                    </div>
                )}
            </div>


        </div>
    );
};

export default DicomGallery;
